/**
 * Vorausberechnung der Umsatzsteuer-Zahllast.
 *
 * Warum das in ein Cashflow-Tool gehoert: bei Soll-Versteuerung entsteht die
 * Umsatzsteuer mit der *Rechnungsstellung*, nicht mit dem Zahlungseingang. Eine
 * Agentur fuehrt also regelmaessig Steuer ab, die sie selbst noch gar nicht
 * vereinnahmt hat. Genau dieser zeitliche Versatz reisst die Liquiditaetsluecken,
 * die man ohne Vorausberechnung erst am 10. bemerkt.
 *
 * Grundlagen: § 18 UStG (Voranmeldung), § 13 UStG (Entstehung), § 20 UStG
 * (Ist-Versteuerung), § 13b UStG (Reverse-Charge), § 108 AO (Fristende am Werktag).
 * Das ersetzt keine Steuerberatung — es prognostiziert Zahlungsstroeme.
 */
import { addMonths, dayInMonth, monthKey, nextBusinessDay } from "./dates.js";
/** Der 10. ist der gesetzliche Stichtag fuer Voranmeldung und Zahlung. */
const FILING_DAY = 10;
/**
 * Ordnet einen Posten seinem Voranmeldungszeitraum zu.
 *
 * Bei Soll-Versteuerung zaehlt das Rechnungsdatum, bei Ist-Versteuerung der
 * Zahlungszeitpunkt. Die Vorsteuer folgt dieser Unterscheidung *nicht*: sie ist
 * in beiden Verfahren mit Erhalt der Rechnung abziehbar, haengt also immer am
 * Rechnungsdatum.
 */
function taxMonthFor(item, scheme) {
    // Eingangsrechnungen (`out`): Vorsteuer ist in beiden Verfahren mit Erhalt
    // der Rechnung abziehbar — immer das Rechnungsdatum.
    if (item.direction === "out")
        return monthKey(item.documentDate);
    // Ausgangsrechnungen (`in`): hier trennen sich die Verfahren.
    if (scheme === "soll")
        return monthKey(item.documentDate);
    // Ist-Versteuerung: massgeblich ist der Zahlungseingang. Ist die Rechnung
    // bezahlt, kennen wir ihn genau; ist sie offen, ist die erwartete Faelligkeit
    // die beste Schaetzung. Den Unterschied macht ein Kunde, der drei Wochen zu
    // spaet zahlt — sonst landet seine Steuer einen Monat zu frueh.
    return monthKey(item.paymentDate ?? item.expectedDate);
}
function quarterKey(month) {
    const q = Math.floor((Number(month.slice(5, 7)) - 1) / 3) + 1;
    return `${month.slice(0, 4)}-Q${q}`;
}
/** Erster Monat des Zeitraums, in dem `month` liegt. */
function periodStartMonth(month, period) {
    if (period === "monthly")
        return month;
    const m = Number(month.slice(5, 7));
    const firstOfQuarter = Math.floor((m - 1) / 3) * 3 + 1;
    return `${month.slice(0, 4)}-${String(firstOfQuarter).padStart(2, "0")}`;
}
function periodLengthMonths(period) {
    return period === "monthly" ? 1 : 3;
}
/**
 * Faelligkeit der Vorauszahlung fuer einen Zeitraum.
 *
 * Regel: 10. Tag nach Ablauf des Zeitraums, bei Dauerfristverlaengerung einen
 * Monat spaeter. Faellt der Tag auf Wochenende oder Feiertag, verschiebt § 108 AO
 * ihn auf den naechsten Werktag.
 */
export function vatDueDate(periodStart, settings, state) {
    const monthsAfter = periodLengthMonths(settings.period) + (settings.permanentExtension ? 1 : 0);
    const dueMonth = addMonths(periodStart, monthsAfter);
    return nextBusinessDay(dayInMonth(dueMonth, FILING_DAY), state);
}
/**
 * Berechnet die Zahllast je Voranmeldungszeitraum aus den geplanten Posten.
 *
 * Beruecksichtigt werden nur Posten mit Steuerbezug: Ausgangsrechnungen
 * (Umsatzsteuer), Eingangsrechnungen und Fixkosten (Vorsteuer). Posten der
 * Quelle `vat` selbst bleiben aussen vor — sonst wuerde die Steuerzahlung
 * in die naechste Steuerberechnung einfliessen.
 */
export function computeVatPeriods(items, settings, options = {}) {
    const buckets = new Map();
    const relevant = items.filter((i) => i.source !== "vat" && i.source !== "buffer" && i.source !== "payroll" && i.status !== "cancelled");
    for (const item of relevant) {
        const start = periodStartMonth(taxMonthFor(item, settings.scheme), settings.period);
        let bucket = buckets.get(start);
        if (!bucket) {
            bucket = emptyPeriod(start, settings, options.state);
            buckets.set(start, bucket);
        }
        // Achtung, zwei Begriffspaare, die sich gerne verwechseln lassen:
        // `direction` ist die *Geldrichtung*, nicht die Rechnungsart. Eine
        // Ausgangsrechnung bringt Geld herein (`in`) und erzeugt Umsatzsteuer;
        // eine Eingangsrechnung kostet Geld (`out`) und erzeugt Vorsteuer.
        if (item.direction === "in") {
            // Ausgangsrechnung. Reverse-Charge-Umsaetze sind mit 0 % ausgewiesen und
            // erzeugen keine Steuer — der Leistungsempfaenger schuldet sie.
            if (!item.reverseCharge)
                bucket.outputVatCents += item.vatCents;
        }
        else if (item.reverseCharge) {
            // Eingangsrechnung mit Steuerschuldumkehr nach § 13b: wir schulden die
            // Steuer und ziehen sie im selben Zeitraum als Vorsteuer wieder ab.
            // Cash-wirksam null, aber beide Seiten muessen ausgewiesen werden.
            const owed = item.vatCents > 0 ? item.vatCents : Math.round((item.netCents * item.vatRatePercent) / 100);
            bucket.reverseChargeOwedCents += owed;
            bucket.reverseChargeDeductibleCents += owed;
        }
        else {
            // Eingangsrechnung ohne Besonderheit → abziehbare Vorsteuer.
            bucket.inputVatCents += item.vatCents;
        }
        bucket.contributingItemIds.push(item.id);
    }
    const results = [...buckets.values()].sort((a, b) => a.periodStart.localeCompare(b.periodStart));
    for (const period of results) {
        period.specialPrepaymentCreditCents = specialPrepaymentCredit(period.periodKey, settings);
        period.liabilityCents =
            period.outputVatCents +
                period.reverseChargeOwedCents -
                period.inputVatCents -
                period.reverseChargeDeductibleCents -
                period.specialPrepaymentCreditCents;
    }
    return results;
}
/**
 * Die Sondervorauszahlung (1/11 der Vorjahres-Zahllast) ist der Preis fuer die
 * Dauerfristverlaengerung: im Februar zu zahlen, in der Dezember-Voranmeldung
 * wieder anzurechnen. Ohne diese Anrechnung waere die Dezember-Prognose um
 * genau diesen Betrag zu hoch.
 */
function specialPrepaymentCredit(periodKey, settings) {
    if (!settings.permanentExtension || settings.period !== "monthly")
        return 0;
    if (!periodKey.endsWith("-12"))
        return 0;
    return Math.round(settings.previousYearLiabilityCents / 11);
}
/** Die im Februar faellige Sondervorauszahlung als eigener Zahlungsposten. */
export function specialPrepaymentItem(year, settings, state) {
    if (!settings.permanentExtension || settings.period !== "monthly")
        return null;
    const amount = Math.round(settings.previousYearLiabilityCents / 11);
    if (amount <= 0)
        return null;
    const due = nextBusinessDay(dayInMonth(`${year}-02`, FILING_DAY), state);
    return {
        id: `vat-special-${year}`,
        source: "vat",
        sourceId: `vat-special-${year}`,
        direction: "out",
        grossCents: amount,
        netCents: amount,
        vatCents: 0,
        vatRatePercent: 0,
        reverseCharge: false,
        expectedDate: due,
        originalDate: due,
        paymentDate: null,
        documentDate: due,
        counterparty: "Finanzamt",
        counterpartyId: null,
        counterpartyIban: settings.taxOfficeIban,
        reference: `Sondervorauszahlung ${year}`,
        category: "tax",
        status: "open",
        matchedCents: 0,
        mocoPaidCents: 0,
        currency: "EUR",
        title: `USt-Sondervorauszahlung ${year} (1/11)`,
        updatedAt: new Date().toISOString(),
    };
}
/**
 * Uebersetzt die berechneten Zahllasten in geplante Posten, damit sie in der
 * Liquiditaetskurve auftauchen und gegen die Finanzamt-Buchung gematcht werden
 * koennen.
 *
 * Eine Erstattung (negative Zahllast) wird zu einem Eingang.
 */
export function vatPlannedItems(periods, settings, options = {}) {
    const now = new Date().toISOString();
    return periods
        .filter((p) => p.liabilityCents !== 0)
        // Zeitraeume, deren Faelligkeit lange zurueckliegt, sind laengst angemeldet
        // und bezahlt. Sie als offene Posten zu fuehren blaeht die Prognose auf und
        // fuellt die Abgleich-Inbox mit Vorschlaegen, zu denen es keine Buchung gibt.
        .filter((p) => !options.notBefore || p.dueDate >= options.notBefore)
        .map((p) => {
        const isRefund = p.liabilityCents < 0;
        const amount = Math.abs(p.liabilityCents);
        return {
            id: `vat-${p.periodKey}`,
            source: "vat",
            sourceId: `vat-${p.periodKey}`,
            direction: isRefund ? "in" : "out",
            grossCents: amount,
            netCents: amount,
            vatCents: 0,
            vatRatePercent: 0,
            reverseCharge: false,
            expectedDate: p.dueDate,
            originalDate: p.dueDate,
            paymentDate: null,
            documentDate: p.periodEnd,
            counterparty: "Finanzamt",
            counterpartyId: null,
            counterpartyIban: settings.taxOfficeIban,
            reference: `USt-VA ${p.periodKey}`,
            category: "tax",
            status: "open",
            matchedCents: 0,
            mocoPaidCents: 0,
            currency: "EUR",
            title: isRefund
                ? `USt-Erstattung ${p.periodKey}`
                : `USt-Vorauszahlung ${p.periodKey}`,
            updatedAt: now,
        };
    });
}
function emptyPeriod(start, settings, state) {
    const lengthMonths = periodLengthMonths(settings.period);
    const endMonth = addMonths(start, lengthMonths - 1);
    return {
        periodKey: settings.period === "monthly" ? start : quarterKey(start),
        periodStart: `${start}-01`,
        periodEnd: lastDayOf(endMonth),
        outputVatCents: 0,
        inputVatCents: 0,
        reverseChargeOwedCents: 0,
        reverseChargeDeductibleCents: 0,
        liabilityCents: 0,
        specialPrepaymentCreditCents: 0,
        dueDate: vatDueDate(start, settings, state),
        contributingItemIds: [],
    };
}
function lastDayOf(month) {
    const year = Number(month.slice(0, 4));
    const m = Number(month.slice(5, 7));
    return new Date(Date.UTC(year, m, 0)).toISOString().slice(0, 10);
}
//# sourceMappingURL=vat.js.map